import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  CardActionArea,
  Container, 
  Typography, 
  Box,
  Card,
  CardContent,
  Avatar,
  useTheme
} from '@mui/material';
import { Straighten } from '@mui/icons-material';

const HomePage: React.FC = () => {
  const theme = useTheme();

  const calculators = [
    { 
      label: 'Calculate Radius', 
      description: 'Find radius and arc length from chord measurements',
      path: '/radius-calc'
    },
    { 
      label: 'Calculate Stair Angle', 
      description: 'Find angles and hypotenuse from rise and run',
      path: '/stair-angle'
    },
    { 
      label: 'Calculate Flare Radius', 
      description: 'Determine radius for flared stair treads',
      path: '/flare-radius'
    },
    { 
      label: 'Calculate Circular Walkway', 
      description: 'Find tread measurements for curved walkways',
      path: '/circular-walkway'
    },
    { 
      label: 'Find Unknown Circular Radius', 
      description: 'Optimize radius for inside/outside measurements',
      path: '/circular-radius'
    },
      { 
      label: 'Calculate HeadRoom', 
      description: 'Determine headroom clearance for stair openings',
      path: '/headroom'
    },
    { 
      label: 'Calculate Baluster Spacing', 
      description: 'Find optimal spacing and count for balusters',
      path: '/baluster-spacing'
    }
  ];

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: 4 
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <Avatar sx={{ 
              bgcolor: theme.palette.primary.main, 
              mr: { xs: 0, sm: 1 },
              width: 64, 
              height: 64 
            }}>
              <Straighten sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2rem', sm: '2.5rem' },
                fontWeight: 700
              }}
            >
              Stair Math Calculator
            </Typography>
          </Box>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}
          >
            Construction calculators for stair design and measurement
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
          >
            Select a calculator below to get started
          </Typography>
        </Box>
        
        {/* Calculator Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: 3
          }}
        >
          {calculators.map((calc) => (
            <Card 
              key={calc.path}
              elevation={2}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardActionArea component={RouterLink} to={calc.path} aria-label={calc.label} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {calc.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {calc.description}
                  </Typography>
                </CardContent>
                <Box sx={{ textAlign: 'center', pb: 3, color: 'primary.main', fontWeight: 600 }} aria-hidden="true">
                  Open calculator →
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Tools for construction and engineering calculations
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
